import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCurrentStore, updateCurrentStore } from '@/lib/api/stores.js';
import { listUsers } from '@/lib/api/users.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

type RemoteMode = 'remote_or_pin' | 'pin_only' | 'not_allowed';

const ACTIONS: Array<{ key: string; label: string; moneyOut?: boolean }> = [
  { key: 'apply_discount', label: 'Apply discount' },
  { key: 'price_override', label: 'Price override' },
  { key: 'waive_deposit', label: 'Waive deposit' },
  { key: 'void_ticket', label: 'Void ticket', moneyOut: true },
  { key: 'issue_refund', label: 'Issue refund', moneyOut: true },
  { key: 'void_payment', label: 'Void payment', moneyOut: true },
  { key: 'delete_ticket', label: 'Delete ticket', moneyOut: true },
];

type ApprovalForm = {
  enabled: boolean;
  actionModes: Record<string, RemoteMode>;
  initialTimeoutSeconds: number;
  onTimeout: 'escalate' | 'auto_deny' | 'keep_waiting';
  escalationTimeoutSeconds: number;
  afterFinalTier: 'auto_deny' | 'keep_waiting';
  tier1: {
    storeManagersHere: boolean;
    storeManagersOther: boolean;
    tenantAdmins: boolean;
    specificUserIds: string[];
    onlyClockedIn: boolean;
  };
  tier2: {
    storeManagersAll: boolean;
    tenantAdmins: boolean;
    specificUserIds: string[];
  };
};

const DEFAULT_FORM: ApprovalForm = {
  enabled: false,
  actionModes: {
    apply_discount: 'remote_or_pin',
    price_override: 'remote_or_pin',
    waive_deposit: 'remote_or_pin',
    void_ticket: 'pin_only',
    issue_refund: 'pin_only',
    void_payment: 'pin_only',
    delete_ticket: 'pin_only',
  },
  initialTimeoutSeconds: 60,
  onTimeout: 'escalate',
  escalationTimeoutSeconds: 90,
  afterFinalTier: 'auto_deny',
  tier1: {
    storeManagersHere: true,
    storeManagersOther: false,
    tenantAdmins: true,
    specificUserIds: [],
    onlyClockedIn: false,
  },
  tier2: {
    storeManagersAll: true,
    tenantAdmins: true,
    specificUserIds: [],
  },
};

function ModeRadios({
  value,
  onChange,
  moneyOut,
}: {
  value: RemoteMode;
  onChange: (v: RemoteMode) => void;
  moneyOut?: boolean;
}) {
  return (
    <div className="space-y-1">
      {(
        [
          ['remote_or_pin', 'Remote or PIN'],
          ['pin_only', 'PIN only'],
          ['not_allowed', 'Not allowed'],
        ] as const
      ).map(([id, label]) => (
        <label key={id} className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="radio"
            checked={value === id}
            onChange={() => onChange(id)}
          />
          {label}
        </label>
      ))}
      {moneyOut && value === 'remote_or_pin' ? (
        <p className="mt-1 text-xs text-amber-800">
          ⚠ Remote approval for cash-out actions can enable fraud without seeing the customer.
          Prefer PIN only unless you trust remote management.
        </p>
      ) : null}
    </div>
  );
}

export function ApprovalsSettingsPage() {
  const qc = useQueryClient();
  const storeQ = useQuery({ queryKey: ['current-store'], queryFn: getCurrentStore });
  const usersQ = useQuery({
    queryKey: ['users-for-approvals'],
    queryFn: () => listUsers(),
  });
  const [form, setForm] = useState<ApprovalForm>(DEFAULT_FORM);

  useEffect(() => {
    if (!storeQ.data?.approvalSettings) return;
    const s = storeQ.data.approvalSettings as Partial<ApprovalForm>;
    setForm({
      ...DEFAULT_FORM,
      ...s,
      actionModes: { ...DEFAULT_FORM.actionModes, ...(s.actionModes ?? {}) },
      tier1: { ...DEFAULT_FORM.tier1, ...(s.tier1 ?? {}) },
      tier2: { ...DEFAULT_FORM.tier2, ...(s.tier2 ?? {}) },
    });
  }, [storeQ.data]);

  const saveMut = useMutation({
    mutationFn: () => updateCurrentStore({ approvalSettings: form as unknown as Record<string, unknown> }),
    onSuccess: () => {
      toastSuccess('Approval settings saved');
      void qc.invalidateQueries({ queryKey: ['current-store'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const staff = usersQ.data ?? [];

  return (
    <div className="space-y-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div>
        <h2 className="text-xl font-bold text-slate-950">Remote Approval</h2>
        <p className="mt-1 text-sm text-slate-500">
          Allow managers to approve override requests from another device when they are not at
          the terminal.
        </p>
      </div>

      <label className="flex items-center gap-3 text-sm font-semibold text-slate-800">
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
        />
        Enable remote approval
      </label>

      <section className="space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">
          Allowed remotely
        </h3>
        {ACTIONS.map((a) => (
          <div
            key={a.key}
            className="grid gap-2 border-b border-slate-100 pb-3 sm:grid-cols-[180px_1fr]"
          >
            <div className="text-sm font-semibold text-slate-800">{a.label}</div>
            <ModeRadios
              value={form.actionModes[a.key] ?? 'pin_only'}
              moneyOut={a.moneyOut}
              onChange={(v) =>
                setForm((f) => ({
                  ...f,
                  actionModes: { ...f.actionModes, [a.key]: v },
                }))
              }
            />
          </div>
        ))}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">
          Timeout & escalation
        </h3>
        <label className="flex items-center gap-2 text-sm">
          Initial timeout (seconds)
          <input
            type="number"
            min={15}
            max={600}
            className="w-24 rounded-lg border border-slate-300 px-2 py-1"
            value={form.initialTimeoutSeconds}
            onChange={(e) =>
              setForm((f) => ({ ...f, initialTimeoutSeconds: Number(e.target.value) || 60 }))
            }
          />
        </label>
        <div className="space-y-1 text-sm">
          <p className="font-medium text-slate-700">On timeout</p>
          {(
            [
              ['escalate', 'Escalate to next tier'],
              ['auto_deny', 'Auto-deny'],
              ['keep_waiting', 'Keep waiting (no expiry)'],
            ] as const
          ).map(([id, label]) => (
            <label key={id} className="flex items-center gap-2">
              <input
                type="radio"
                checked={form.onTimeout === id}
                onChange={() => setForm((f) => ({ ...f, onTimeout: id }))}
              />
              {label}
            </label>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm">
          Escalation timeout (seconds)
          <input
            type="number"
            min={15}
            max={600}
            className="w-24 rounded-lg border border-slate-300 px-2 py-1"
            value={form.escalationTimeoutSeconds}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                escalationTimeoutSeconds: Number(e.target.value) || 90,
              }))
            }
          />
        </label>
        <div className="space-y-1 text-sm">
          <p className="font-medium text-slate-700">After final tier</p>
          {(
            [
              ['auto_deny', 'Auto-deny'],
              ['keep_waiting', 'Keep waiting'],
            ] as const
          ).map(([id, label]) => (
            <label key={id} className="flex items-center gap-2">
              <input
                type="radio"
                checked={form.afterFinalTier === id}
                onChange={() => setForm((f) => ({ ...f, afterFinalTier: id }))}
              />
              {label}
            </label>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">
          Who gets notified — Tier 1
        </h3>
        {(
          [
            ['storeManagersHere', 'Store managers at this location'],
            ['storeManagersOther', 'Store managers at other locations'],
            ['tenantAdmins', 'Tenant admins'],
            ['onlyClockedIn', 'Only users currently signed in (active session)'],
          ] as const
        ).map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.tier1[key]}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  tier1: { ...f.tier1, [key]: e.target.checked },
                }))
              }
            />
            {label}
          </label>
        ))}
        <div>
          <p className="mb-1 text-sm font-medium text-slate-700">Specific users</p>
          <select
            multiple
            className="min-h-[100px] w-full max-w-md rounded-lg border border-slate-300 px-2 py-1 text-sm"
            value={form.tier1.specificUserIds}
            onChange={(e) => {
              const ids = Array.from(e.target.selectedOptions).map((o) => o.value);
              setForm((f) => ({ ...f, tier1: { ...f.tier1, specificUserIds: ids } }));
            }}
          >
            {staff.map((u: { id: string; firstName: string; lastName: string }) => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">
          Tier 2 (after escalation)
        </h3>
        {(
          [
            ['storeManagersAll', 'Store managers at ALL locations'],
            ['tenantAdmins', 'Tenant admins'],
          ] as const
        ).map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.tier2[key]}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  tier2: { ...f.tier2, [key]: e.target.checked },
                }))
              }
            />
            {label}
          </label>
        ))}
      </section>

      <button
        type="button"
        className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
        disabled={saveMut.isPending}
        onClick={() => saveMut.mutate()}
      >
        Save approval settings
      </button>
    </div>
  );
}
